import {Helmet} from '@dr.pogodin/react-helmet';
import {Action} from 'platform/components';

import {useEffect, useRef} from 'react';

import {isFalse, isNilOrEmpty} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import {openCreateTaskFormDialog, openEditTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {taskManagerRoutes, testIds} from '@dms/routes';
import {Main, PageTab, usePermissions} from '@dms/shared';

import {buildArray, useQueryState} from 'shared/web';

import {TaskCalendar} from './(sections)/TaskCalendar/TaskCalendar';
import {TasksDataGrid, refreshTaskGrid} from './(sections)/TasksDataGrid/TasksDataGrid';

export function TaskOverview() {
  const [canCreateTask] = usePermissions({
    permissionKeys: ['createTask'],
  });

  const [taskId] = useQueryState('taskId');

  const wasTaskDetailShown = useRef(isNilOrEmpty(taskId));

  useEffect(() => {
    const MOUNT_DELAY_MS = 100;
    let timeout: ReturnType<typeof setTimeout>;

    if (taskId && isFalse(wasTaskDetailShown.current)) {
      timeout = setTimeout(() => {
        openEditTaskFormDialog({
          id: taskId,
          onEdit: refreshTaskGrid,
          'data-testid': testIds.taskManager.taskOverview('editTaskDialog'),
        });
        wasTaskDetailShown.current = true;
      }, MOUNT_DELAY_MS);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [taskId]);

  const Actions = buildArray<Action>().when(canCreateTask, {
    type: 'button',
    title: i18n.t('entity.task.actions.createTask'),
    variant: 'primary',
    'data-testid': testIds.taskManager.taskOverview('createTask'),
    onClick: () =>
      openCreateTaskFormDialog({
        onCreate: refreshTaskGrid,
        'data-testid': testIds.taskManager.taskOverview('createTaskDialog'),
      }),
  });

  const Tabs = buildArray<PageTab>()
    .add({
      id: 'task-list',
      title: i18n.t('entity.task.labels.tasks'),
      href: taskManagerRoutes.taskOverview,
      content: <TasksDataGrid data-testid={testIds.taskManager.taskOverview('grid')} />,
    })
    .whenFeatureEnabled(featureFlags.CORE_TASK_CALENDAR, {
      id: 'task-calendar',
      title: i18n.t('entity.task.labels.tasksCalendar'),
      href: taskManagerRoutes.taskCalendar,
      content: <TaskCalendar />,
    });

  return (
    <>
      <Helmet title={i18n.t('entity.task.labels.taskManagement')} />
      <Main
        isFullHeight
        data-testid={testIds.taskManager.taskOverview()}
        actions={Actions}
        tabs={Tabs}
      />
    </>
  );
}
